import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Bookmark, ChevronRight, CalendarCheck } from 'lucide-react';
import { fmtRevenue1, fmtInt } from '../lib/fieldInfo';
import { CodeNameTag } from './CodeNameTag';

function scorePct(score, max) {
  if (score === null || score === undefined || !max) return null;
  return Math.round((score / max) * 100);
}

function bandColor(band, score) {
  const b = (band || '').toLowerCase();
  if (b === 'high' || (score != null && score >= 80)) return 'text-emerald-400';
  if (b === 'medium' || (score != null && score >= 55)) return 'text-amber-400';
  return 'text-muted-foreground';
}

// Shared compact deal table for "My Favorites" and "My Bookmarks" — Explorer-style columns.
export function DealMiniTable({ deals, type, onRemove }) {
  const navigate = useNavigate();
  const RemoveIcon = type === 'favorite' ? Star : Bookmark;
  const activeColor = type === 'favorite' ? 'text-amber-400' : 'text-blue-400';

  return (
    <div className="border border-border rounded-sm" data-testid={`${type}-table`}>
      <table className="w-full text-left table-fixed">
        <thead>
          <tr className="bg-secondary/50 text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
            <th className="py-2.5 px-3 w-[26%]">Project</th>
            <th className="py-2.5 px-3 w-[20%]">Sector</th>
            <th className="py-2.5 px-3 text-right w-[10%]">Rev €m</th>
            <th className="py-2.5 px-3 text-right w-[8%]">Empl</th>
            <th className="py-2.5 px-3 w-[13%] hidden md:table-cell">Stage</th>
            <th className="py-2.5 px-3 w-[13%] hidden lg:table-cell">Succession</th>
            <th className="py-2.5 px-3 w-[5%]"></th>
          </tr>
        </thead>
        <tbody>
          {deals.map((p, i) => {
            const p1 = scorePct(p.phase1_score, p.phase1_max);
            return (
              <tr key={p.id} data-testid={`${type}-row-${i}`}
                onClick={() => navigate(`/projects/${p.id}`)}
                className="border-t border-border even:bg-secondary/40 row-hover-glow cursor-pointer">
                <td className="py-3 px-3">
                  <div className="flex items-center gap-2">
                    <button onClick={(e) => { e.stopPropagation(); onRemove(p.id); }} data-testid={`${type}-remove-${i}`}
                      className={`shrink-0 ${activeColor} hover:opacity-70 transition-opacity`} title="Remove">
                      <RemoveIcon className="w-3.5 h-3.5" fill="currentColor" />
                    </button>
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-foreground truncate block">{p.name}</span>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {p.random_project_name && <CodeNameTag name={p.random_project_name} block className="text-[10px] text-blue-400/70 font-mono truncate" />}
                      {p.on_agenda && (
                        <span title="On the team meeting agenda" data-testid={`${type}-agenda-chip-${i}`}
                          className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded border border-purple-500/30 bg-purple-500/10 text-purple-400 font-mono uppercase">
                          <CalendarCheck className="w-2.5 h-2.5" /> Agenda
                        </span>
                      )}
                    </div>
                  </div>
                  </div>
                </td>
                <td className="py-3 px-3">
                  <div className="text-xs text-foreground/80 truncate">{p.sector || <span className="text-muted-foreground/40">—</span>}</div>
                  {p.subsector && <div className="text-[10px] text-muted-foreground truncate">{p.subsector}</div>}
                </td>
                <td className="py-3 px-3 text-xs text-foreground/70 font-mono text-right">{fmtRevenue1(p.revenue) || '—'}</td>
                <td className="py-3 px-3 text-xs text-foreground/70 font-mono text-right">{fmtInt(p.employees) || '—'}</td>
                <td className="py-3 px-3 text-xs text-muted-foreground hidden md:table-cell truncate">{p.stage_label || '—'}</td>
                <td className="py-3 px-3 text-xs hidden lg:table-cell truncate">
                  {p.succession_score != null ? (
                    <span className={`font-mono font-medium ${bandColor(p.succession_band, p.succession_score)}`}>
                      {p.succession_score}<span className="text-muted-foreground/40">/100</span>
                    </span>
                  ) : p1 != null ? (
                    <span className="font-mono text-muted-foreground">P1 {p1}%</span>
                  ) : <span className="text-muted-foreground/30">—</span>}
                </td>
                <td className="py-3 px-3 text-right"><ChevronRight className="w-3 h-3 text-muted-foreground/20 inline" /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
